'use client'

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import posthog from 'posthog-js'
import { AlumniaLogo } from '@/components/alumnia-logo'

/**
 * Global Error Boundary — catches errors in the root layout itself.
 * Must include its own <html> and <body> tags because the root layout
 * is unavailable when this component renders.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
    posthog.captureException(error)
  }, [error])

  return (
    <html lang="en">
      <body className="flex min-h-screen items-center justify-center bg-gray-50 px-4 font-sans antialiased">
        <div className="mx-auto w-full max-w-md text-center">
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside root layout; <Link> may not work */}
          <a
            href="/"
            className="mb-10 inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            <AlumniaLogo size={40} />
          </a>

          <div className="mb-8 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100 shadow-sm ring-1 ring-red-200/60">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>

          <h1 className="mb-2 text-2xl font-semibold text-gray-900">Something went wrong</h1>
          <p className="mb-6 text-sm text-gray-600">
            An unexpected error occurred. Our team has been notified and is working on a fix.
          </p>

          {error.digest && <p className="mb-4 text-xs text-gray-500">Error ID: {error.digest}</p>}

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={reset}
              className="inline-flex h-10 items-center justify-center rounded-md bg-blue-900 px-6 text-sm font-medium text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Try again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside root layout; <Link> may not work */}
            <a
              href="/"
              className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 bg-white px-6 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Go home
            </a>
          </div>
        </div>
      </body>
    </html>
  )
}
